/**
 * ORBC mint/burn conversion page.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { apAPI } from '../api/ap';
import { uocAPI } from '../api/uoc';
import { usersAPI } from '../api/users';
import { useAuthStore } from '../store/auth';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedField, ThemedInput, ThemedMetric, ThemedNotice, ThemedPanel, ThemedSelect, ThemedSubmitButton } from '../components/ui/ThemedSurface';
import { tokenDisplayLabel } from '@/utils/orb-display';

interface ConfigRow {
  id: number;
  target_token: string;
  pool_balance: string;
  rate: string;
  is_active: boolean;
}

interface SupplyData {
  total_supply: string;
  current_supply: string;
  update_time: string;
}

interface ConfigList {
  total: number;
  rows: ConfigRow[];
}

interface MintResult {
  ref: string;
  from_asset: string;
  amount: string;
  uoc_credited: string;
}

interface BurnResult {
  ref: string;
  to_asset: string;
  uoc_burned: string;
  target_received: string;
  rate: string;
}

const TOKEN_LABEL: Record<string, string> = {
  ep1: 'EP1',
  ep2: 'EP2',
  rp: 'RP',
  tp: 'TP',
  ep: 'EP',
  uoc: tokenDisplayLabel('uoc'),
};

function SupplySummaryPanel({ supply }: { supply?: SupplyData }) {
  const { t } = useTranslation();
  const totalSupply = supply ? Number(supply.total_supply).toLocaleString() : '—';
  const currentSupply = supply ? Number(supply.current_supply).toLocaleString() : '—';

  return (
    <ThemedPanel className="overflow-hidden p-0">
      <div className="relative px-4 py-4">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold} 50%, transparent 100%)` }} />
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-medium" style={{ color: C.muted }}>{t('uocConvert.totalSupply')}</div>
            <div className="mt-1 flex items-end gap-1">
              <span className="truncate font-mono text-[28px] font-semibold leading-none" style={{ color: C.gold, textShadow: `0 0 14px ${C.gold}24` }}>
                {totalSupply}
              </span>
              <span className="pb-0.5 text-xs font-semibold" style={{ color: C.muted }}>ORBC</span>
            </div>
          </div>
          <div className="rounded-full border px-3 py-1 text-[10px] font-semibold" style={{ borderColor: C.softBorder, background: C.chipBg, color: C.gold }}>
            SUPPLY
          </div>
        </div>

        <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-xl border px-3 py-3" style={{ borderColor: C.softBorder, background: C.inputBg }}>
          <div className="min-w-0">
            <div className="text-[10px] leading-none" style={{ color: C.muted }}>{t('uocConvert.currentCirculation')}</div>
            <div className="mt-1 truncate font-mono text-base font-semibold leading-none" style={{ color: C.text }}>
              {currentSupply}
            </div>
          </div>
          <div className="h-9 w-px" style={{ background: C.statDividerAlt }} />
          <div className="min-w-0 text-right">
            <div className="text-[10px] leading-none" style={{ color: C.muted }}>{t('uocConvert.updateTime')}</div>
            <div className="mt-1 truncate font-mono text-xs font-semibold leading-none" style={{ color: C.gold }}>
              {supply?.update_time ? new Date(supply.update_time).toLocaleDateString() : '—'}
            </div>
          </div>
        </div>
      </div>
    </ThemedPanel>
  );
}

export function UocConvert() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile);
  const [mintAsset, setMintAsset] = useState<'ep1' | 'ep2'>('ep1');
  const [mintAmount, setMintAmount] = useState('');
  const [burnAsset, setBurnAsset] = useState<'rp' | 'tp' | 'ep'>('rp');
  const [burnAmount, setBurnAmount] = useState('');
  const [mintDropdownOpen, setMintDropdownOpen] = useState(false);
  const [burnDropdownOpen, setBurnDropdownOpen] = useState(false);
  const [toUsername, setToUsername] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferMemo, setTransferMemo] = useState('');
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurity, setShowSecurity] = useState(true);
  const [fpAmount, setFpAmount] = useState('');
  const [donateMemo, setDonateMemo] = useState('');
  const [donateTxPwd, setDonateTxPwd] = useState('');
  const [donateSecAnswer, setDonateSecAnswer] = useState('');
  const [showDonateSecurity, setShowDonateSecurity] = useState(true);

  const { data: configs } = useQuery({
    queryKey: ['uoc', 'configs'],
    queryFn: async () =>
      (await uocAPI.getConfigs({ page: 1, limit: 10 })) as unknown as ConfigList,
    refetchInterval: 30_000,
  });

  const { data: supply } = useQuery({
    queryKey: ['uoc', 'supply'],
    queryFn: async () => (await uocAPI.getSupply()) as unknown as SupplyData,
    refetchInterval: 30_000,
  });

  const { data: balances } = useQuery({
    queryKey: ['user', 'balances'],
    queryFn: () => usersAPI.getBalances(),
    refetchInterval: 15_000,
  });

  const { data: apPreview } = useQuery({
    queryKey: ['ap', 'donate-uo', 'preview'],
    queryFn: () => apAPI.donatePreview(),
    staleTime: 30_000,
  });

  const { data: transferMine } = useQuery({
    queryKey: ['uoc', 'transfer-out', 'mine'],
    queryFn: async () =>
      (await uocAPI.transferOutMine({ page: 1, limit: 10 })) as unknown as {
        total: number;
        rows: Array<{ id: string; to_username: string; amount_uoc: string; status: string; create_time: string }>;
      },
  });

  const uocBal = balances?.balances?.uoc ?? '0';
  const securityQuestion =
    ((profile as Record<string, unknown> | null)?.security_question as string | undefined) ??
    t('convert.defaultSecurityQuestion');

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['uoc'] });
    qc.invalidateQueries({ queryKey: ['user'] });
    qc.invalidateQueries({ queryKey: ['ap'] });
  }

  const mintMut = useMutation({
    mutationFn: (body: { from_asset: string; amount: string }) => uocAPI.mint(body) as unknown as Promise<MintResult>,
    onSuccess: (res) => {
      setMintAmount('');
      invalidate();
      alert(`${t('uocConvert.mintSuccess')}${TOKEN_LABEL[res.from_asset]} ${res.amount} → ORBC ${res.uoc_credited}`);
    },
    onError: (err: unknown) => alert(err instanceof ApiError ? err.message : t('uocConvert.mintFailed')),
  });

  const burnMut = useMutation({
    mutationFn: (body: { to_asset: string; amount: string }) => uocAPI.burn(body) as unknown as Promise<BurnResult>,
    onSuccess: (res) => {
      setBurnAmount('');
      invalidate();
      alert(`${t('uocConvert.convertSuccess')}ORBC ${res.uoc_burned} → ${TOKEN_LABEL[res.to_asset]} ${res.target_received} (rate=${res.rate})`);
    },
    onError: (err: unknown) => alert(err instanceof ApiError ? err.message : t('uocConvert.convertFailed')),
  });

  const transferOutMut = useMutation({
    mutationFn: () =>
      uocAPI.transferOut({
        to_username: toUsername.trim(),
        amount_uoc: transferAmount.trim(),
        memo: transferMemo.trim() || undefined,
        transaction_password: txPassword.trim(),
        security_answer: securityAnswer.trim(),
      }),
    onSuccess: () => {
      setToUsername('');
      setTransferAmount('');
      setTransferMemo('');
      setTxPassword('');
      setSecurityAnswer('');
      invalidate();
      alert(t('uocConvert.transferOutSuccess'));
    },
    onError: (err: unknown) => alert(err instanceof ApiError ? err.message : t('uocConvert.transferOutFailed')),
  });

  const donateMut = useMutation({
    mutationFn: () =>
      apAPI.donate({
        fp_amount: fpAmount.trim(),
        transaction_password: donateTxPwd.trim(),
        security_answer: donateSecAnswer.trim(),
        memo: donateMemo.trim() || undefined,
      }),
    onSuccess: () => {
      setFpAmount('');
      setDonateMemo('');
      setDonateTxPwd('');
      setDonateSecAnswer('');
      invalidate();
      alert(t('uocConvert.donateSuccess'));
    },
    onError: (err: unknown) => alert(err instanceof ApiError ? err.message : t('uocConvert.donateFailed')),
  });

  const activeConfigs = configs?.rows?.filter((config) => config.is_active) ?? [];
  const burnConfig = activeConfigs.find((config) => config.target_token === burnAsset);
  const burnPreview = burnConfig && burnAmount && Number(burnAmount) > 0 ? (Number(burnAmount) * Number(burnConfig.rate)).toFixed(6) : null;
  const mintOptions: { value: 'ep1' | 'ep2'; label: string }[] = [
    { value: 'ep1', label: 'EP1' },
    { value: 'ep2', label: 'EP2' },
  ];
  const burnOptions = activeConfigs.length > 0
    ? activeConfigs.map((config) => ({
      value: config.target_token as 'rp' | 'tp' | 'ep',
      label: TOKEN_LABEL[config.target_token] ?? config.target_token.toUpperCase(),
    }))
    : [{ value: burnAsset, label: t('uocConvert.noChannels'), disabled: true }];

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('uocConvert.title')} onBack={() => navigate(-1)} theme={C} />
        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]" style={{ height: 'calc(100% - 70px)', paddingBottom: '12px' }}>
          <ThemedNotice tone="info">{t('uocConvert.withdrawFlowNote')}</ThemedNotice>
          <SupplySummaryPanel supply={supply} />

          <ThemedPanel className="overflow-visible p-4">
            <div className="mb-1 text-sm font-semibold" style={{ color: C.text }}>{t('uocConvert.mintUoc')}</div>
            <div className="mb-3 text-xs" style={{ color: C.muted }}>{t('uocConvert.mintDesc')}</div>
            <div className="grid grid-cols-[96px_1fr] gap-2">
              <ThemedSelect
                value={mintAsset}
                options={mintOptions}
                open={mintDropdownOpen}
                onToggle={() => {
                  setMintDropdownOpen((open) => !open);
                  setBurnDropdownOpen(false);
                }}
                onSelect={(value) => {
                  setMintAsset(value);
                  setMintDropdownOpen(false);
                }}
              />
              <ThemedInput type="number" placeholder={t('common.amount')} value={mintAmount} onChange={(e) => setMintAmount(e.target.value)} min="0" step="any" />
            </div>
            {mintAmount && Number(mintAmount) > 0 && (
              <div className="mt-3 rounded-xl border px-3 py-2 text-xs" style={{ borderColor: C.line, background: C.inputBg, color: C.muted }}>
                {t('uocConvert.willReceive')}: <span className="font-mono" style={{ color: C.gold }}>{mintAmount} ORBC</span>
              </div>
            )}
            <div className="mt-3">
              <ThemedSubmitButton disabled={mintMut.isPending || !mintAmount || Number(mintAmount) <= 0} onClick={() => mintMut.mutate({ from_asset: mintAsset, amount: mintAmount })}>
                {mintMut.isPending ? t('common.processing') : t('uocConvert.mintUoc')}
              </ThemedSubmitButton>
            </div>
          </ThemedPanel>

          <ThemedPanel className="overflow-visible p-4">
            <div className="mb-1 text-sm font-semibold" style={{ color: C.text }}>{t('uocConvert.convertUoc')}</div>
            <div className="mb-3 text-xs" style={{ color: C.muted }}>{t('uocConvert.convertDesc')}</div>
            <div className="grid grid-cols-[96px_1fr] gap-2">
              <ThemedSelect
                value={burnAsset}
                options={burnOptions}
                open={burnDropdownOpen}
                disabled={activeConfigs.length === 0}
                placeholder={t('uocConvert.noChannels')}
                onToggle={() => {
                  if (activeConfigs.length === 0) return;
                  setBurnDropdownOpen((open) => !open);
                  setMintDropdownOpen(false);
                }}
                onSelect={(value) => {
                  setBurnAsset(value);
                  setBurnDropdownOpen(false);
                }}
              />
              <ThemedInput type="number" placeholder={`ORBC ${t('common.amount')}`} value={burnAmount} onChange={(e) => setBurnAmount(e.target.value)} min="0" step="any" />
            </div>
            {burnConfig ? (
              <div className="mt-3 flex flex-col gap-2">
                <ThemedMetric label={t('uocConvert.convertRate')} value={`1 ORBC = ${burnConfig.rate} ${TOKEN_LABEL[burnAsset]}`} color={C.gold} />
                <ThemedMetric label={t('uocConvert.poolBalance')} value={`${Number(burnConfig.pool_balance).toLocaleString()} ${TOKEN_LABEL[burnAsset]}`} />
                {burnPreview && <ThemedNotice tone="success">{t('uocConvert.estimatedReceive')}: {burnPreview} {TOKEN_LABEL[burnAsset]}</ThemedNotice>}
              </div>
            ) : null}
            <div className="mt-3">
              <ThemedSubmitButton disabled={burnMut.isPending || !burnAmount || Number(burnAmount) <= 0 || !burnConfig} onClick={() => burnMut.mutate({ to_asset: burnAsset, amount: burnAmount })}>
                {burnMut.isPending ? t('common.processing') : `${t('uocConvert.convertTo')} ${TOKEN_LABEL[burnAsset]}`}
              </ThemedSubmitButton>
            </div>
          </ThemedPanel>

          {activeConfigs.length > 0 && (
            <ThemedPanel className="p-4">
              <div className="mb-3 text-sm font-semibold" style={{ color: C.text }}>{t('uocConvert.currentChannels')}</div>
              <div className="flex flex-col gap-2">
                {activeConfigs.map((config) => (
                  <div key={config.id} className="grid grid-cols-[1fr_auto_auto] gap-2 rounded-xl border px-3 py-2 text-xs" style={{ borderColor: C.line, background: C.inputBg }}>
                    <span style={{ color: C.text }}>ORBC → {TOKEN_LABEL[config.target_token]}</span>
                    <span className="font-mono" style={{ color: C.gold }}>{config.rate}</span>
                    <span className="font-mono" style={{ color: C.muted }}>{Number(config.pool_balance).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </ThemedPanel>
          )}

          <ThemedPanel className="p-4">
            <div className="mb-1 text-sm font-semibold" style={{ color: C.text }}>{t('uocConvert.withdrawTitle')}</div>
            <div className="mb-3 text-xs" style={{ color: C.muted }}>{t('uocConvert.withdrawDesc')}</div>
            <ThemedMetric label={t('uocConvert.uocBalance')} value={uocBal} color={C.gold} />
            <div className="mt-3 flex flex-col gap-3">
              <ThemedField label={t('uocConvert.toUsername')} required>
                <ThemedInput value={toUsername} onChange={(e) => setToUsername(e.target.value)} placeholder={t('transfer.recipientPlaceholder', 'username')} />
              </ThemedField>
              <ThemedField label={`ORBC ${t('common.amount')}`} required>
                <ThemedInput type="number" min="0" step="any" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)} />
              </ThemedField>
              <ThemedField label={t('transfer.memo', '备注')}>
                <ThemedInput value={transferMemo} onChange={(e) => setTransferMemo(e.target.value)} />
              </ThemedField>
              <TransactionSecurity
                txPassword={txPassword}
                onTxPasswordChange={setTxPassword}
                showSecurity={showSecurity}
                onShowSecurityChange={setShowSecurity}
                securityAnswer={securityAnswer}
                onSecurityAnswerChange={setSecurityAnswer}
                securityQuestionText={securityQuestion}
              />
            </div>
            <div className="mt-3">
              <ThemedSubmitButton
                disabled={
                  transferOutMut.isPending ||
                  !toUsername.trim() ||
                  !transferAmount.trim() ||
                  !txPassword.trim() ||
                  !securityAnswer.trim()
                }
                onClick={() => transferOutMut.mutate()}
              >
                {transferOutMut.isPending ? t('common.processing') : t('uocConvert.withdrawTitle')}
              </ThemedSubmitButton>
            </div>
          </ThemedPanel>

          {(transferMine?.rows?.length ?? 0) > 0 && (
            <ThemedPanel className="p-4">
              <div className="mb-3 text-sm font-semibold" style={{ color: C.text }}>{t('uocConvert.myTransferOut')}</div>
              <div className="flex flex-col gap-2">
                {transferMine!.rows.map((row) => (
                  <div key={row.id} className="rounded-xl border px-3 py-2 text-xs" style={{ borderColor: C.line, background: C.inputBg }}>
                    <div className="font-mono" style={{ color: C.text }}>→ {row.to_username}</div>
                    <div className="mt-1" style={{ color: C.gold }}>{row.amount_uoc} ORBC · {row.status}</div>
                    <div className="mt-0.5" style={{ color: C.muted }}>{row.create_time}</div>
                  </div>
                ))}
              </div>
            </ThemedPanel>
          )}

          <ThemedPanel className="p-4">
            <div className="mb-1 text-sm font-semibold" style={{ color: C.text }}>{t('uocConvert.apDonateTitle')}</div>
            <div className="mb-3 text-xs" style={{ color: C.muted }}>{t('uocConvert.apDonateDesc')}</div>
            {apPreview && (
              <div className="mb-3 grid grid-cols-2 gap-2">
                <ThemedMetric label={t('uocConvert.apAvailable')} value={String(apPreview.ap_available ?? '—')} />
                <ThemedMetric label={t('uocConvert.fpBalance')} value={String(apPreview.fp_balance ?? '—')} />
                <ThemedMetric label={t('uocConvert.uoBalance')} value={String(apPreview.uo_balance ?? '—')} />
              </div>
            )}
            <div className="flex flex-col gap-3">
              <ThemedField label={t('uocConvert.fpAmount')} required>
                <ThemedInput type="number" min="0" step="any" value={fpAmount} onChange={(e) => setFpAmount(e.target.value)} />
              </ThemedField>
              <ThemedField label={t('transfer.memo', '备注')}>
                <ThemedInput value={donateMemo} onChange={(e) => setDonateMemo(e.target.value)} />
              </ThemedField>
              <TransactionSecurity
                txPassword={donateTxPwd}
                onTxPasswordChange={setDonateTxPwd}
                showSecurity={showDonateSecurity}
                onShowSecurityChange={setShowDonateSecurity}
                securityAnswer={donateSecAnswer}
                onSecurityAnswerChange={setDonateSecAnswer}
                securityQuestionText={securityQuestion}
              />
            </div>
            <div className="mt-3">
              <ThemedSubmitButton
                disabled={donateMut.isPending || !fpAmount.trim() || !donateTxPwd.trim() || !donateSecAnswer.trim()}
                onClick={() => donateMut.mutate()}
              >
                {donateMut.isPending ? t('common.processing') : t('uocConvert.apDonateTitle')}
              </ThemedSubmitButton>
            </div>
          </ThemedPanel>
        </div>
      </div>
    </div>
  );
}
